<template>
  <div class="educational-material-box">
    <!-- Educational Materials View -->
    <educational-material-list
      v-if="showEducationalMaterials"
      ref="educationalMaterialList"
      :current-folder="currentFolder"
      :tumor-sites="tumorSites"
      @folder-changed="$emit('folder-changed')"
      @open-type-selector="openTypeSelector"
      @edit-item="editItem"
      @share-button-click="onShareButtonClick"
      @move-to-public="handleMoveToPublic"
    />

    <!-- Assigned Users View -->
    <assigned-users-list
      v-else-if="showAssignedUsers"
      ref="assignedUsersList"
      :current-folder="currentFolder"
      @folder-changed="$emit('folder-changed')"
      @view-responses="handleViewResponses"
      @edit-assignment="handleEditAssignment"
    />

    <!-- Responses View -->
    <responses-from-recipients
      v-else-if="showResponsesFromRecipients"
      ref="responsesFromRecipients"
      :current-folder="currentFolder"
      @folder-changed="$emit('folder-changed')"
    />

    <!-- Shared Modals and Components -->
    <educational-material-add
      :edit-id="selectedEditId"
      :type="selectedType"
      :selected-user-id="selectedUserId"
      :current-folder="currentFolder"
      :tumor-sites="tumorSites"
      @saved="handleMaterialSaved"
    />

    <edu-material-copy
      :system-id-number="systemIdNumber"
      :creation-id="creationId"
      :show="showCopyModal"
      @closed="handleCopyModalClosed"
    />

    <!-- Create Type Modal -->
    <b-modal
      id="create-type-modal"
      title="Create Educational Material"
      hide-footer
    >
      <div class="d-flex flex-column align-items-center">
        <b-button
          block
          variant="primary"
          class="mb-2 btn-primary px-4 rounded-pill"
          @click="proceedToForm('Collection')"
        >
          Tumor Case Collection
        </b-button>

        <b-button
          block
          variant="primary"
          class="mb-2 btn-primary px-4 rounded-pill"
          @click="proceedToForm('Assignment')"
        >
          Assignment
        </b-button>

        <b-button
          block
          variant="primary"
          class="mb-2 btn-primary px-4 rounded-pill"
          @click="proceedToForm('Presentation')"
        >
          Presentation
        </b-button>
      </div>

      <div class="text-right mt-3">
        <b-button
          variant="secondary"
          @click="$bvModal.hide('create-type-modal')"
        >
          Cancel
        </b-button>
      </div>
    </b-modal>

    <!-- Share Popover Container -->
    <div class="share-popover-container" style="position: fixed; z-index: 1001">
      <div
        ref="sharePopoverEl"
        class="edit-case-menu shadow-toolbar"
        v-if="sharePopover.show"
        :style="{
          top: sharePopover.y,
          left: sharePopover.x,
          position: 'fixed',
          display: 'block',
          width: '240px',
          maxWidth: 'calc(100vw - 20px)',
          maxHeight: 'calc(100vh - 20px)',
          overflowY: 'auto',
          visibility: sharePopover.positioned ? 'visible' : 'hidden',
        }"
        @click.stop
      >
        <div class="popover-content text-center position-relative">
          <div class="toolbar-close-icon" @click="closeSharePopover">
            <svg
              id="Icon_ionic-md-close-circle-outline"
              data-name="Icon ionic-md-close-circle-outline"
              xmlns="http://www.w3.org/2000/svg"
              width="16.062"
              height="16.062"
              viewBox="0 0 16.062 16.062"
            >
              <path
                id="Path_10189"
                data-name="Path 10189"
                d="M11.406,5A6.406,6.406,0,1,1,6.873,6.873,6.383,6.383,0,0,1,11.406,5m0-1.622a8.031,8.031,0,1,0,8.031,8.031,8.03,8.03,0,0,0-8.031-8.031Z"
                transform="translate(-3.375 -3.375)"
                fill="#204c90"
              />
              <path
                id="Path_10190"
                data-name="Path 10190"
                d="M18.718,17.595l-1.124,1.124L14.7,15.826l-2.892,2.892-1.124-1.124L13.579,14.7l-2.892-2.892,1.124-1.124L14.7,13.579l2.892-2.892,1.124,1.124L15.826,14.7Z"
                transform="translate(-6.672 -6.672)"
                fill="#204c90"
              />
            </svg>
          </div>
          <div class="d-flex flex-column">
            <label for="material-link-expiration" class="text-left mb-1">
              Link expires after
            </label>
            <b-form-select
              id="material-link-expiration"
              v-model="linkExpiration"
              size="sm"
              style="font-size:13px !important"
              :options="[
                { value: 'two-weeks', text: '2 weeks (default)' },
                { value: 'custom', text: 'Custom number of days' },
                { value: 'forever', text: 'Forever' },
              ]"
            />
            <b-form-input
              v-if="linkExpiration === 'custom'"
              v-model="linkExpirationDays"
              class="mt-2"
              type="number"
              min="1"
              step="1"
              size="sm"
              aria-label="Number of days before link expires"
              placeholder="Number of days"
            />
            <b-btn
              class="m-1 text-nowrap btn-rounded"
              variant="primary"
              size="sm"
              @click="shareMaterial('qr')"
            >
              Generate QR code
            </b-btn>
            <b-btn
              class="m-1 text-nowrap btn-rounded"
              variant="primary"
              size="sm"
              @click="shareMaterial('link')"
            >
              Copy link
            </b-btn>
            <b-btn
              class="m-1 text-nowrap btn-rounded"
              variant="primary"
              size="sm"
              @click="(event) => openAssignUserModal(event)"
              v-if="sharePopover.material.type === 'Assignment'"
            >
              Assign To Users
            </b-btn>
            <b-btn
              v-if="sharePopover.material.type === 'Assignment'"
              class="m-1 text-nowrap btn-rounded"
              variant="primary"
              size="sm"
              :disabled="answerPdfGenerating"
              @click.stop="shareAnswerPdf"
            >
              {{ answerPdfGenerating ? "Generating..." : "Copy Answers PDF link" }}
            </b-btn>
          </div>
        </div>
      </div>
    </div>

    <!-- Assign Popover -->
    <div
      v-if="assignPopover.show"
      ref="assignPopoverEl"
      class="share-popover-container edit-case-menu assign-popover"
      :style="{
        position: 'fixed',
        top: assignPopover.y,
        left: assignPopover.x,
        zIndex: 1002,
        visibility: assignPopover.positioned ? 'visible' : 'hidden',
      }"
    >
      <div class="assign-popover__scroll">
        <assign-to-user-form
          :assignment="assignPopover.assignment"
          @assign="handleAssignToUsers"
          @resend-existing="handleResendToExistingUsers"
          @close="assignPopover.show = false"
        />
      </div>
    </div>

    <!-- Hidden canvas for QR code -->
    <canvas ref="qrCanvas" style="display: none"></canvas>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import * as folderTypes from "../../../constants/constants-folder";
import * as roleTypes from "../../../constants/constants-role";
import QRCode from "qrcode";
import { buildEducationalMaterialShortLinkData } from "../utils/material-short-link";
import ShareAssignmentAnswerMixin from "../../../mixins/share-assignment-answer-mixin";

// Import child components
import EducationalMaterialList from "./educational-materials/EducationalMaterialList.vue";
import AssignedUsersList from "./educational-materials/AssignedUsersList.vue";
import ResponsesFromRecipients from "./educational-materials/ResponsesFromRecipients.vue";
import EducationalMaterialAdd from "../components/add-educational-material.vue";
import AssignToUserForm from "../components/assign-to-user-form.vue";
import EduMaterialCopy from "../../../components/common/edu-material-copy.vue";

export default {
  name: "EducationalMaterialBox",
  mixins: [ShareAssignmentAnswerMixin],
  data() {
    // const userRole = localStorage.getItem("userRole")?.toUpperCase();
    // const isTenantAdmin = userRole === "TENANT ADMIN";
    return {
      ...folderTypes,
      ...roleTypes,
      tumorSites: [],
      userDetail: null,
      tumorSiteId: null,
      selectedType: null,
      selectedEditId: null,
      selectedUserId: null,
      systemIdNumber: null,
      creationId: null,
      showCopyModal: false,
      linkExpiration: "two-weeks",
      linkExpirationDays: 14,
      answerPdfGenerating: false,
      sharePopover: {
        anchor: null,
        material: null,
        show: false,
        positioned: false,
        x: "",
        y: "",
      },
      assignPopover: {
        show: false,
        x: "",
        y: "",
        assignment: null,
        positioned: false,
      },
    };
  },
  components: {
    EducationalMaterialList,
    AssignedUsersList,
    ResponsesFromRecipients,
    EducationalMaterialAdd,
    AssignToUserForm,
    EduMaterialCopy,
  },
  props: {
    currentFolder: {
      type: Object,
      default: null,
    },
  },
  computed: {
    showEducationalMaterials() {
      return (
        this.currentFolder &&
        this.currentFolder.folderType !== "ASSIGNEDTOUSERS" &&
        this.currentFolder.folderType !== "RESPONSEFROMRECIPIENTSCHILD"
      );
    },
    showAssignedUsers() {
      return (
        this.currentFolder &&
        this.currentFolder.folderType === "ASSIGNEDTOUSERS"
      );
    },
    showResponsesFromRecipients() {
      return (
        this.currentFolder &&
        this.currentFolder.folderType === "RESPONSEFROMRECIPIENTSCHILD"
      );
    },
  },
  watch: {
    linkExpiration() {
      if (this.sharePopover.show) {
        this.positionSharePopover();
      }
    },
    currentFolder: {
      handler(newFolder) {
        if (newFolder) {
          console.log("Current folder changed:", newFolder);
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    window.addEventListener("click", this.closeSharePopover);
    await this.initializeData();
  },
  beforeDestroy() {
    window.removeEventListener("click", this.closeSharePopover);
  },
  methods: {
    ...mapActions(["setSelectedCaseSlideIds"]),

    async fetchFolderMaterials() {
      await this.$nextTick();

      if (this.showEducationalMaterials && this.$refs.educationalMaterialList) {
        return this.$refs.educationalMaterialList.fetchFolderMaterials();
      }

      if (this.showAssignedUsers && this.$refs.assignedUsersList) {
        return this.$refs.assignedUsersList.fetchAssignedMaterials();
      }

      if (
        this.showResponsesFromRecipients &&
        this.$refs.responsesFromRecipients
      ) {
        return this.$refs.responsesFromRecipients.fetchRecipientsList();
      }
    },

    async initializeData() {
      try {
        const storedTumorSites = localStorage.getItem('tumorSites');
        if (storedTumorSites) {
          this.tumorSites = JSON.parse(storedTumorSites);
        } else {
          await this.getUserDetails();
        }

        this.initialLoading = false;

        if (this.currentFolder) {
          await this.fetchFolderMaterials(this.currentFolder);
        }
      } catch (error) {
        console.error("Error initializing data:", error);
        this.$handleError(error);
      }
    },

    async getUserDetails() {
      try {
        const response = await this.$api.getUserDetailFact(
          localStorage.getItem("userId")
        );
        this.userDetail = response.data;

        if (this.userDetail?.userTumorSites) {
          this.tumorSiteId = this.userDetail.userTumorSites.id;
          await this.getAllMetadataList(this.tumorSiteId);
        } else {
          this.tumorSiteId = null;
        }

        return this.userDetail;
      } catch (error) {
        this.$handleError(error);
        throw error;
      }
    },

    async getAllMetadataList(tumorSiteId) {
      try {
        const response = await this.$api.getAllMetadataListFact(tumorSiteId);
        const allDataList = response.data.payload;
        if (allDataList != null) {
          this.tumorSites = allDataList.tumorSites;
        }
        return allDataList;
      } catch (error) {
        this.$handleError(error);
        throw error;
      }
    },

    handleGridModeChange(mode) {
      this.gridMode = mode;
    },
    handleSizeChange(params) {
      this.paginationParams.size = params.size;
      this.paginationParams.page = 0;
      if (this.currentFolder && !this.initialLoading) {
        this.fetchFolderMaterials(this.currentFolder);
      }
    },
    handlePageChange(params) {
      this.paginationParams.page = params.page;
      if (this.currentFolder && !this.initialLoading) {
        this.fetchFolderMaterials(this.currentFolder);
      }
    },
    handleFilterChange(newFilters) {
      const userRole = localStorage.getItem("userRole")?.toUpperCase();
    const isTenantAdmin = userRole === "TENANT ADMIN";
      if (Object.keys(newFilters).length === 0) {
    this.currentFilters = {
      search: "",
      slideCollectionType: "",
      level: "",
      tumorSiteIdsList: isTenantAdmin
        ? localStorage.getItem("tumorSiteIdsString") || null
        : localStorage.getItem("tumorSiteId"),
      isRecommended: null,
    };
  } else {
    this.currentFilters = {
      ...this.currentFilters,
      ...newFilters,
    };
  }
      
      this.paginationParams.page = 0;
      if (this.currentFolder && !this.initialLoading) {
        this.fetchFolderMaterials(this.currentFolder);
      }
    },

    async viewMaterialInfo(eduMaterial) {
      var query;
      var isPublicSite = this.currentFolder.folderType === "EDUCATIONALPUBLIC" ? true : false;
      const route = "edu-case-viewer";
      this.$changeRouteToTab({
        name: route,
        params: {
          id: eduMaterial.id,
          isPublicSite,
          tumorSiteId: eduMaterial.tumorSiteId,
          tumorSiteName: eduMaterial.tumorSite,
          type: eduMaterial.type,
          backToPublicSite: false,
          isMaterialPublished: eduMaterial.isShowPublicSite
        },
        query: query ? query.query : null,
      });
    },
    openTypeSelector() {
      this.$bvModal.show("create-type-modal");
    },

    proceedToForm(type) {
      this.selectedType = type;
      this.selectedEditId = null;
      this.selectedUserId = null;
      this.$bvModal.hide("create-type-modal");
      this.$bvModal.show("create-material-modal");
    },

    handleMaterialSaved() {
      this.$bvModal.hide("create-material-modal");
      this.fetchFolderMaterials();
      this.$emit("material-saved");
    },

    handleCopyModalClosed() {
      this.showCopyModal = false;
      this.selectedMaterial = null;
      this.systemIdNumber = null;
      this.creationId = null;
    },

    editItem(item) {
      this.selectedEditId = item.id;
      this.selectedType = item.type;
      this.selectedUserId = item.author;
      this.$bvModal.show("create-material-modal");
    },

    handleMoveToPublic(data) {
      this.systemIdNumber = data.systemIdNumber;
      this.creationId = data.creationId;
      this.showCopyModal = true;
    },

    closeSharePopover() {
      this.sharePopover = {
        anchor: null,
        material: null,
        show: false,
        positioned: false,
        x: "",
        y: "",
      };
    },

    onShareButtonClick(event, material) {
      if (!event) {
        console.error("Event is undefined!");
        return;
      }

      this.selectedMaterial = material;
      this.linkExpiration = "two-weeks";
      this.linkExpirationDays = 14;

      this.sharePopover = {
        anchor: event,
        material,
        show: true,
        positioned: false,
        x: "0px",
        y: "0px",
      };
      this.positionSharePopover();
    },

    positionSharePopover(popover = this.sharePopover, refName = "sharePopoverEl") {
      this.$nextTick(() => {
        const el = this.$refs[refName];
        if (popover !== this.sharePopover || !popover.show || !el) return;

        const rect = popover.anchor.getBoundingClientRect();
        const { width, height } = el.getBoundingClientRect();
        const spacing = 5;
        const viewportWidth = document.documentElement.clientWidth;
        const viewportHeight = document.documentElement.clientHeight;

        let y = rect.bottom + spacing;
        if (y + height > viewportHeight - spacing) {
          y = rect.top - height - spacing;
        }
        y = Math.max(spacing, Math.min(y, viewportHeight - height - spacing));

        const x = Math.max(
          spacing,
          Math.min(rect.right - width, viewportWidth - width - spacing)
        );

        popover.x = `${x}px`;
        popover.y = `${y}px`;
        popover.positioned = true;
      });
    },

    async shareAnswerPdf() {
      if (this.answerPdfGenerating || !this.sharePopover.material) return;
      const expirationDays = this.selectedLinkExpirationDays();
      if (expirationDays === undefined) return;
      const popover = this.sharePopover;
      this.answerPdfGenerating = true;
      try {
        const link = await this.createAssignmentAnswerLink(popover.material.id, expirationDays);
        if (!link || this.sharePopover !== popover || !popover.show) return;
        try {
          await navigator.clipboard.writeText(new URL(link.url, window.location.origin).href);
          this.$toastr("success", "Answer PDF link copied to clipboard.");
        } catch (error) {
          this.$toastr("error", "Unable to copy the Answer PDF link. Please try again.");
        }
      } finally {
        this.answerPdfGenerating = false;
      }
    },

    selectedLinkExpirationDays() {
      const expirationDays = this.linkExpiration === "forever"
        ? null
        : this.linkExpiration === "custom" ? Number(this.linkExpirationDays) : 14;
      if (expirationDays !== null &&
          (!Number.isSafeInteger(expirationDays) || expirationDays < 1)) {
        this.$toastr("error", "Enter a positive whole number of days.");
        return undefined;
      }
      return expirationDays;
    },

    async shareMaterial(type) {
      const isMaterialShare = type === "qr" || type === "link";
      const expirationDays = isMaterialShare ? this.selectedLinkExpirationDays() : undefined;
      if (isMaterialShare && expirationDays === undefined) return;
      this.closeSharePopover();

      if (!this.selectedMaterial) return;
      console.log("Sharing material:", this.selectedMaterial);
      const material = this.selectedMaterial;
      const encryptedShareUrl = buildEducationalMaterialShortLinkData(
        material,
        this.$encryptData,
        {
          isPublicSite: true,
          tumorSiteId:
            material.tumorSiteId || material.tumorSite?.id ||
            localStorage.getItem("tumorSiteId") || 1,
        }
      );

      try {
        const response = await this.$api.postCaseUrl({
          encryptedData: encryptedShareUrl,
          ...(isMaterialShare ? { educationalMaterialLink: true, expirationDays } : {}),
        });
        if (!response?.data?.id) {
          throw new Error("Failed to generate share link");
        }
        const shortUrl = `${window.location.origin}/edu-short-links/${response.data.id}`;
        if (type === "assigntouser") {
          return { shortUrl, responseId: response.data.id };
        } else if (type === "qr") {
          const canvas = this.$refs.qrCanvas;
          await QRCode.toCanvas(canvas, shortUrl, { width: 300 });
          const link = document.createElement("a");
          link.href = canvas.toDataURL("image/png");
          const fileName = `${
            material.systemIdNumber || material.idNumber
          }.png`;
          link.download = fileName;
          link.click();
        } else {
          await navigator.clipboard.writeText(shortUrl);
          this.$toastr("success", "Copied to clipboard");
        }
      } catch (error) {
        console.error("Failed to create short link:", error);
        this.$toastr("error", error.response?.data?.message || "Failed to generate share link");
      }
    },

    showAssignPopoverAt(anchor, assignment) {
      // Keep the row element, not the position of the intermediate Share menu.
      const initialRect = anchor.getBoundingClientRect();
      this.assignPopover = {
        show: true,
        x: "0px",
        y: "0px",
        assignment,
        positioned: false,
      };

      // Each opening owns its measurement; an older callback must not move it.
      const popover = this.assignPopover;
      this.$nextTick(() => {
        const el = this.$refs.assignPopoverEl;
        if (this.assignPopover !== popover || !popover.show || !el) return;

        const spacing = 8;
        const vw = document.documentElement.clientWidth;
        const vh = document.documentElement.clientHeight;

        // Constrain before measuring, including on viewports with scrollbars.
        el.style.maxWidth = `${Math.max(0, vw - spacing * 2)}px`;
        el.style.maxHeight = `${Math.max(0, vh - spacing * 2)}px`;
        const { width, height } = el.getBoundingClientRect();
        const rect = anchor.isConnected
          ? anchor.getBoundingClientRect()
          : initialRect;

        // Prefer the right side, then the left, and shift inward if neither fits.
        let x = rect.right + spacing;
        if (x + width > vw - spacing) {
          x = rect.left - width - spacing;
        }
        x = Math.max(spacing, Math.min(x, vw - width - spacing));
        // Keep an edge attached to this button, rather than pinning every lower
        // row to the viewport bottom. Center only when neither alignment fits.
        let y = rect.top;
        if (y + height > vh - spacing) {
          y = rect.bottom - height;
          if (y < spacing) {
            y = (rect.top + rect.bottom - height) / 2;
          }
        }
        y = Math.max(spacing, Math.min(y, vh - height - spacing));

        popover.x = `${x}px`;
        popover.y = `${y}px`;
        popover.positioned = true;
      });
    },

    openAssignUserModal(event) {
      const anchor = this.sharePopover.anchor || event.currentTarget;
      this.showAssignPopoverAt(anchor, null);
      this.closeSharePopover();
    },

    async handleAssignToUsers(payload) {
      try {
        if (payload.isEditMode) {
          this.selectedMaterial = {
            id: payload.id,
            type: payload.type,
            tumorSiteId: payload.tumorSiteId,
            tumorSiteName: payload.tumorSiteName,
            isShowPublicSite: payload.isShowPublicSite,
          };
        }
        const result = await this.shareMaterial("assigntouser");

        if (!result) {
          this.$toastr("error", "Share link generation failed");
          return;
        }
        const { shortUrl, responseId } = result;
        console.log("Short URL for assignment:", shortUrl, responseId, payload);
        const response = await this.$api.assignMaterialToUsers({
          materialId: this.selectedMaterial.id,
          deadlineDate: payload.deadline,
          startDate: payload.startDate,
          receivePdfCopy: payload.includePdf,
          numberOfAttempts: payload.numberOfAttempts,
          recipientEmails: payload.emails,
          shortlink: shortUrl,
          randomId: responseId,
        });
        if (this.$handleResponse && !this.$handleResponse(response)) {
          return false;
        }
        this.$toastr("success", "Assigned successfully!");
        this.$emit("material-assigned");
      } catch (e) {
        this.$handleError(e);
      }
    },

    async handleResendToExistingUsers(payload) {
      try {
        const response = await this.$api.reassignMaterialToUsers({
          assignmentId: payload.assignmentId,
          materialId: payload.materialId,
          deadlineDate: payload.deadline,
          receivePdfCopy: payload.includePdf,
          numberOfAttempts: payload.numberOfAttempts,
          resendRecipientEmails: payload.emails,
          randomId: payload.randomId,
        });

        if (this.$handleResponse && !this.$handleResponse(response)) {
          return false;
        }
        this.$toastr("success", "Resent successfully!");
        this.$emit("material-assigned");
      } catch (e) {
        this.$handleError(e);
      }
    },

    handleEditAssignment(payload) {
      const { event, assignment } = payload;
      this.showAssignPopoverAt(event, assignment);
    },

    handleViewResponses(payload) {
      this.$emit("view-responses", payload);
    },
  },
};
</script>

<style lang="scss" scoped>
.share-popover-container.edit-case-menu {
  position: absolute;
}
.assign-popover {
  width: max-content;
  height: 340px;
  max-height: calc(100vh - 16px);
  padding: 0;
  border-radius: 16px;
  overflow: hidden;
}
.assign-popover__scroll {
  max-width: 100%;
  height: 100%;
  padding: 12px 16px;
  overflow-y: auto;
  box-sizing: border-box;
}
.b-calendar .b-calendar-nav .btn {
  font-size: 10px !important;
}
</style>
